// src/components/Tax Pro Only/EmailModal/EmailModal.tsx
import React, { useEffect } from "react";
import { Modal } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import "./EmailModal.scss";
import { IconMailForward, IconX } from "@tabler/icons-react";
import { useEmailStore } from "@components/Tax Pro Only/Modals/EmailModal/emailStore.ts";
import EmailController from "@components/Tax Pro Only/Modals/EmailModal/EmailController/EmailController.tsx";
import ConfirmCloseModal from "@components/ConfirmCloseModal/ConfirmCloseModal.tsx";
import { notifications } from "@mantine/notifications";
import { createName } from "@components/Tax Pro Only/Modals/EmailModal/utils.ts";
import type { Client } from "@/types";

interface EmailModalProps {
  /**
   * The page that is calling this email modal.
   */
  source: "clientProfile" | "clientList";
  /**
   * The target of the email if source is 'clientProfile'
   */
  targetUser?: Client;
  /**
   * Whether the email modal should be opened.
   */
  emailOpened: boolean;
  /**
   * Hook called when action to close modal has been called.
   */
  closeEmail: () => void;
  onEmailSent?: () => void;
}

const EmailModal: React.FC<EmailModalProps> = ({
  targetUser, // Name of client.
  source,
  emailOpened, // Whether modal is opened.
  closeEmail,
  onEmailSent = () => {},
}) => {
  const setRecipients = useEmailStore((state) => state.setRecipients);
  const setSource = useEmailStore((state) => state.setSource);
  const resetState = useEmailStore((state) => state.resetState);
  const loadTemplates = useEmailStore((state) => state.loadTemplates);
  const [confirmOpened, { open: openConfirm, close: closeConfirm }] =
    useDisclosure(false);

  const handleModalClose = () => {
    openConfirm();
  };

  const handleConfirmClose = () => {
    closeConfirm();
    resetState();
    closeEmail();
  };

  useEffect(() => {
    if (emailOpened) {
      // When modal opens, load templates
      loadTemplates().catch((error) => {
        console.error("Error loading templates in EmailModal:", error);
      });

      // Set recipients if target user is provided
      if (targetUser) {
        setRecipients([
          {
            name: createName(targetUser.firstName, targetUser.lastName),
            email: targetUser.email,
          },
        ]);
      }

      // Set source
      setSource(source);
    }
  }, [
    emailOpened,
    loadTemplates,
    setRecipients,
    setSource,
    source,
    targetUser,
  ]);

  // Checks that if source is meant to have a single profile that one is given.
  if (emailOpened && source === "clientProfile" && targetUser === undefined) {
    notifications.show({
      title: "Something went wrong, please refresh and try again.",
      message: "Client profile is not defined.",
      color: "red",
    });
    closeEmail();
  }

  return (
    <>
      <Modal
        opened={emailOpened}
        onClose={handleModalClose}
        centered
        overlayProps={{
          backgroundOpacity: 0.55,
          blur: 3,
        }}
        withCloseButton={false}
        padding="1.5rem"
        size="90%"
        className="email-modal"
        title={
          <span className="email-modal-title">
            <span className="email-modal-title-icon">
              <IconMailForward size={24} />
              <h3>Send email</h3>
            </span>
            <IconX size={24} onClick={handleModalClose} />
          </span>
        }
      >
        <EmailController onEmailSent={onEmailSent} />
      </Modal>
      <ConfirmCloseModal
        modalOpened={confirmOpened}
        closeModal={handleConfirmClose}
        handleSelectNo={closeConfirm}
      />
    </>
  );
};

export default EmailModal;
